// APD Copilot settings: connected Gmail mailboxes, who they are shared with,
// and the advanced model choice. Server decides every permission; this page
// only shows what the server allows.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plug, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import type { ApiError } from '@/lib/api';
import { disconnectMailbox, getCopilotModels, getMailboxes, getTeamMembers, grantMailbox, revokeMailboxGrant, startGmailConnect } from '@/lib/copilot-api';
import { useCopilot } from '@/lib/copilot-context';

export const CONNECT_ERRORS: Record<string, string> = {
  denied: 'Google sign-in was cancelled.',
  state: 'That connection link expired. Start again.',
  wrong_account: 'You signed in to Google as a different account. Connect your own APD mailbox.',
  not_allowed: 'Your role cannot connect this mailbox.',
  not_company_domain: 'Only a company mailbox can be connected as shared.',
  is_personal: "That address belongs to a colleague. They connect it themselves and can share it with you.",
  conflict: 'That mailbox is already connected differently.',
  no_refresh: 'Google did not grant offline access. Try again and accept every permission.',
  scopes: 'Google did not grant read access to the mailbox.',
  not_configured: 'Gmail is not configured on this deployment yet.',
  no_key: 'Credential encryption is not configured on this deployment.',
  exchange: 'Google refused the sign-in. Try again.',
  profile: 'Could not confirm which mailbox you signed in to.',
  missing: 'Google returned an incomplete response. Try again.',
  server: 'Something went wrong. Try again.',
};

export const MODEL_KEY = 'copilot:model';

export function CopilotSettingsPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { capability } = useCopilot();
  const boxes = useQuery({ queryKey: ['copilot', 'mailboxes'], queryFn: () => getMailboxes(() => getToken()), enabled: capability.canUse });
  const team = useQuery({ queryKey: ['team', 'members'], queryFn: () => getTeamMembers(() => getToken()), enabled: capability.canUse });
  const models = useQuery({ queryKey: ['copilot', 'models'], queryFn: () => getCopilotModels(() => getToken()), enabled: capability.canUse });
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [model, setModel] = useState(() => { try { return localStorage.getItem(MODEL_KEY) ?? 'auto'; } catch { return 'auto'; } });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get('gmail');
    if (g === 'connected') setNotice({ ok: true, text: 'Mailbox connected.' });
    else if (g === 'error') setNotice({ ok: false, text: CONNECT_ERRORS[p.get('reason') ?? ''] ?? 'The mailbox was not connected.' });
  }, []);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['copilot', 'mailboxes'] });
  const connect = useMutation({ mutationFn: (kind: 'personal' | 'shared') => startGmailConnect(kind, () => getToken()), onSuccess: (r) => { window.location.href = r.url; } });
  const disconnect = useMutation({ mutationFn: (id: string) => disconnectMailbox(id, () => getToken()), onSuccess: refresh });
  const grant = useMutation({ mutationFn: (a: { id: string; membershipId: string; canDraft: boolean }) => grantMailbox(a.id, { membershipId: a.membershipId, canRead: true, canDraft: a.canDraft }, () => getToken()), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: (a: { id: string; grantId: string }) => revokeMailboxGrant(a.id, a.grantId, () => getToken()), onSuccess: refresh });
  const err = (connect.error ?? disconnect.error ?? grant.error ?? revoke.error) as unknown as ApiError | null;

  if (!capability.canUse) {
    return <AppShell title="APD Copilot"><div className="p-6 text-sm text-foreground-muted">APD Copilot is not enabled for this workspace.</div></AppShell>;
  }
  const data = boxes.data;
  const me = team.data?.members.find((m) => m.isSelf);
  const hasPersonal = data?.manageable.some((m) => m.kind === 'personal' && m.status === 'connected');
  return (
    <AppShell title="APD Copilot" subtitle="Mailboxes and settings">
      <div className="h-full overflow-auto">
        <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6">
          {notice && <div className={`rounded-md p-3 text-sm ${notice.ok ? 'bg-success-muted' : 'bg-danger-muted'}`}>{notice.text}</div>}
          {err && <div className="rounded-md p-3 text-sm bg-danger-muted">{err.message}</div>}

          <section className="card p-4 space-y-3">
            <div className="flex items-center gap-2 font-semibold"><Mail className="w-4 h-4" />Gmail</div>
            {!data?.gmailConfigured ? (
              <p className="text-sm text-foreground-muted">Gmail is not configured on this deployment yet. Copilot still reads the email APD has already linked to each RFQ.</p>
            ) : (
              <>
                <p className="text-sm text-foreground-muted">Copilot can search and read connected mailboxes and create drafts you review. It never sends email. You can disconnect at any time, here or in your Google account.</p>
                <div className="flex flex-wrap gap-2">
                  {!hasPersonal && <button className="btn-primary text-sm" disabled={connect.isPending} onClick={() => connect.mutate('personal')}><Plug className="w-4 h-4 inline mr-1" />Connect my mailbox{me ? ` (${me.email})` : ''}</button>}
                  {data.canManageShared && <button className="btn-outline text-sm" disabled={connect.isPending} onClick={() => connect.mutate('shared')}>Connect a shared mailbox (e.g. rfq@)</button>}
                </div>
              </>
            )}

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-foreground-muted">Mailboxes you can use</div>
              {(data?.accessible ?? []).length === 0 && <div className="text-sm text-foreground-muted">None yet.</div>}
              {(data?.accessible ?? []).map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium break-all">{m.email}</span>
                  <span className="pill pill-default">{m.kind === 'shared' ? 'Shared' : m.via === 'owner' ? 'Yours' : 'Shared with you'}</span>
                  <span className={`pill ${m.status === 'connected' ? 'pill-success' : 'pill-warning'}`}>{m.status === 'connected' ? (m.canDraft ? 'Read + drafts' : 'Read only') : 'Reconnect needed'}</span>
                </div>
              ))}
            </div>
          </section>

          {(data?.manageable ?? []).map((m) => {
            const others = (team.data?.members ?? []).filter((x) => !x.isSelf && x.role !== 'viewer' && !m.grants.some((g) => g.membershipId === x.membershipId));
            return (
              <section key={m.id} className="card p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold break-all">{m.email}</span>
                  <span className={`pill ${m.status === 'connected' ? 'pill-success' : 'pill-warning'}`}>{m.status}</span>
                  {m.status !== 'revoked' && <button className="ml-auto text-xs text-destructive inline-flex items-center gap-1" onClick={() => disconnect.mutate(m.id)}><Trash2 className="w-3.5 h-3.5" />Disconnect</button>}
                </div>
                {m.lastError && <div className="text-xs text-warning-foreground">{m.lastError}</div>}
                <div className="text-xs text-foreground-muted">{m.kind === 'personal' ? 'Only you can read this mailbox unless you share it below.' : 'Only the people listed below can use this shared mailbox.'}</div>
                <ul className="space-y-1 text-sm">
                  {m.grants.map((g) => (
                    <li key={g.id} className="flex flex-wrap items-center gap-2">
                      <span>{g.name}</span><span className="pill pill-default">{g.canDraft ? 'Read + drafts' : 'Read only'}</span>
                      <button className="text-xs text-destructive" onClick={() => revoke.mutate({ id: m.id, grantId: g.id })}>Remove</button>
                    </li>
                  ))}
                </ul>
                {others.length > 0 && m.status === 'connected' && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    {others.map((o) => (
                      <span key={o.membershipId} className="inline-flex gap-1">
                        <button className="btn-outline text-xs" onClick={() => grant.mutate({ id: m.id, membershipId: o.membershipId, canDraft: false })}>Share read with {o.fullName ?? o.email}</button>
                        <button className="btn-outline text-xs" onClick={() => grant.mutate({ id: m.id, membershipId: o.membershipId, canDraft: true })}>+ drafts</button>
                      </span>
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          <section className="card p-4 space-y-2">
            <div className="font-semibold">Advanced</div>
            <label className="text-sm block space-y-1">
              <span className="text-foreground-muted">Model</span>
              <select className="block w-full sm:w-72 border border-border rounded-md px-2 py-1.5 bg-background text-sm" value={model}
                onChange={(e) => { setModel(e.target.value); try { localStorage.setItem(MODEL_KEY, e.target.value); } catch { /* storage unavailable */ } }}>
                <option value="auto">Auto (recommended)</option>
                {(models.data?.models ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <p className="text-xs text-foreground-muted">Auto picks a fast model for everyday questions and a stronger one for Research Part.</p>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
